import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from 'react-oidc-context';
import { isConfigured } from '../../shared/config/authConfig';
import './LoginPage.css';
import './CallbackPage.css';

/**
 * The four stages of the sign-in handshake, in order.
 *
 * These are not decoration. Each maps to a state the flow is genuinely in, so a
 * stall shows the user — and us — exactly how far it got. A single spinner tells
 * you nothing about whether the code was exchanged.
 */
const NUM_COLS = 90;
const HEX_COLS = Array.from({ length: NUM_COLS }, (_, i) => i);
const HEX_ROWS = Array.from({ length: 20 });

const STEPS = ['Redirected', 'Verifying token', 'Starting session', 'Complete'];

/**
 * How long this screen stays up, at minimum, even when the exchange finishes
 * sooner — which it does: the code exchange typically takes a couple of hundred
 * milliseconds, so without this the whole screen flashes past unread.
 *
 * DELIBERATE, not a leftover debug value. Adjust this one constant to change how
 * long sign-in dwells here; do not "optimise" it away as an accidental delay.
 * Set to 0 to go back to redirecting the instant the session exists.
 */
const MINIMUM_VISIBLE_MS = 5000;

/**
 * Pacing for the step trail, so the four stages spread across the dwell instead
 * of the screen sitting frozen on one of them.
 *
 * The pacing only controls how fast the display is *allowed* to advance. Real
 * auth state still caps it — see `stage` below — so a step never claims to have
 * happened before it has.
 */
const STEP_INTERVAL_MS = MINIMUM_VISIBLE_MS / STEPS.length;

/**
 * Where Microsoft sends the user back to: /callback?code=…&state=…
 *
 * The exchange itself is not done here. react-oidc-context sees the code and
 * state on mount and performs it, which matters for two reasons the handoff
 * calls out:
 *
 *   - `state` must be verified before anything else. Nothing on the backend
 *     checks it, because there is no backend callback, so this is the only CSRF
 *     protection in the flow. The library compares it against the value it
 *     stored and refuses to continue on a mismatch.
 *   - A code is single-use. StrictMode double-invokes effects in development, so
 *     a hand-rolled useEffect fires the exchange twice and the second attempt
 *     fails with invalid_grant. index.js does run StrictMode, so this is a real
 *     condition here, not a theoretical one. The library guards it.
 *
 * This component therefore only reports progress and decides where to go next.
 */
function CallbackPage() {
  const navigate = useNavigate();

  // Undefined when SsoProvider found no config and so rendered no provider —
  // which is prodops' normal state (cognito_enabled = false). Optional chaining
  // throughout rather than destructuring, so reaching this route in an
  // environment without SSO shows the message below instead of crashing.
  const auth = useAuth();

  // How far the trail is allowed to have advanced, on pacing alone.
  const [pacedStep, setPacedStep] = useState(1);
  // Whether the minimum dwell has elapsed.
  const [dwellElapsed, setDwellElapsed] = useState(MINIMUM_VISIBLE_MS === 0);

  useEffect(() => {
    const paceTimer = setInterval(
      () => setPacedStep((step) => Math.min(step + 1, STEPS.length - 1)),
      STEP_INTERVAL_MS
    );
    const dwellTimer = setTimeout(() => setDwellElapsed(true), MINIMUM_VISIBLE_MS);

    return () => {
      clearInterval(paceTimer);
      clearTimeout(dwellTimer);
    };
  }, []);

  useEffect(() => {
    // Leave only once BOTH are true: the session exists, and the screen has been
    // up long enough to read. `replace` so Back does not return to a /callback
    // URL whose code has already been spent.
    //
    // An error does not wait — a failure should surface immediately rather than
    // making someone watch a progress trail that is going nowhere.
    if (auth?.isAuthenticated && dwellElapsed) navigate('/dashboard', { replace: true });
  }, [auth?.isAuthenticated, dwellElapsed, navigate]);

  if (!isConfigured()) {
    // Only reachable by typing the URL in an environment with no Cognito pool.
    // Says so plainly rather than redirecting, because a silent bounce to /login
    // looks like a failed sign-in and invites someone to retry it forever.
    return (
      <div className="login-page">
        {/* Full-screen honeycomb hex field */}
        <div className="login-hex-field" aria-hidden="true">
          {HEX_COLS.map((col) => (
            <div
              key={col}
              className={[
                'login-hex-col-field',
                `login-hex-col-v${col % 5}`,
                col % 2 === 1 ? 'login-hex-col--offset' : '',
              ].join(' ')}
            >
              {HEX_ROWS.map((_, i) => (
                <div key={i} className="login-hex-grid-cell" />
              ))}
            </div>
          ))}
        </div>
        <div className="login-bg-highlight login-bg-highlight--1" aria-hidden="true" />
        <div className="login-bg-highlight login-bg-highlight--2" aria-hidden="true" />
        <div className="login-bg-highlight login-bg-highlight--3" aria-hidden="true" />
        <div className="login-card login-card--visible">
          <div className="login-header">
            <img
              src="/command_quality_pty_ltd_logo.jpg"
              alt="Command Quality"
              className="login-logo"
            />
            <h1 className="login-title">Microsoft sign-in is not enabled here</h1>
            <p className="login-subtitle">
              This environment has no Cognito pool configured. Sign in with your username
              and password instead.
            </p>
          </div>

          <button className="btn btn-primary" onClick={() => navigate('/login', { replace: true })}>
            Back to sign in
          </button>
        </div>
      </div>
    );
  }

  if (auth?.error) {
    return (
      <div className="login-page">
        {/* Full-screen honeycomb hex field */}
        <div className="login-hex-field" aria-hidden="true">
          {HEX_COLS.map((col) => (
            <div
              key={col}
              className={[
                'login-hex-col-field',
                `login-hex-col-v${col % 5}`,
                col % 2 === 1 ? 'login-hex-col--offset' : '',
              ].join(' ')}
            >
              {HEX_ROWS.map((_, i) => (
                <div key={i} className="login-hex-grid-cell" />
              ))}
            </div>
          ))}
        </div>
        <div className="login-bg-highlight login-bg-highlight--1" aria-hidden="true" />
        <div className="login-bg-highlight login-bg-highlight--2" aria-hidden="true" />
        <div className="login-bg-highlight login-bg-highlight--3" aria-hidden="true" />
        <div className="login-card login-card--visible">
          <div className="login-header">
            <img
              src="/command_quality_pty_ltd_logo.jpg"
              alt="Command Quality"
              className="login-logo"
            />
            <h1 className="login-title">Sign-in failed</h1>
            <p className="login-subtitle">We could not complete your Microsoft sign-in.</p>
          </div>

          {/* Shown rather than swallowed: the two likely causes — a state
              mismatch and a reused or expired code — are indistinguishable from
              a blank screen, and both are recoverable by starting again. */}
          <div className="alert alert-error" role="alert">
            {auth.error.message}
          </div>

          <button className="btn btn-primary" onClick={() => navigate('/login', { replace: true })}>
            Back to sign in
          </button>
        </div>
      </div>
    );
  }

  // How far the trail may show. Pacing decides how fast it moves; real auth
  // state decides how far it is allowed to go, so the display can lag reality
  // but never lead it.
  //
  // Reaching this component at all means the redirect happened, so step 0 is
  // always behind us. Until a session exists the trail stops at "Verifying
  // token" — the later steps have genuinely not happened yet.
  const furthestTruthfulStep = auth?.isAuthenticated ? STEPS.length - 1 : 1;
  const stage = Math.min(pacedStep, furthestTruthfulStep);

  return (
    <div className="login-page">
      {/* Full-screen honeycomb hex field */}
      <div className="login-hex-field" aria-hidden="true">
        {HEX_COLS.map((col) => (
          <div
            key={col}
            className={[
              'login-hex-col-field',
              `login-hex-col-v${col % 5}`,
              col % 2 === 1 ? 'login-hex-col--offset' : '',
            ].join(' ')}
          >
            {HEX_ROWS.map((_, i) => (
              <div key={i} className="login-hex-grid-cell" />
            ))}
          </div>
        ))}
      </div>
      <div className="login-bg-highlight login-bg-highlight--1" aria-hidden="true" />
      <div className="login-bg-highlight login-bg-highlight--2" aria-hidden="true" />
      <div className="login-bg-highlight login-bg-highlight--3" aria-hidden="true" />
      <div className="cb-panel">
        <img
          src="/command_quality_pty_ltd_logo.jpg"
          alt="Command Quality"
          className="cb-logo"
        />

        <h1 className="cb-title">
          Verifying your <span className="cb-title-accent">identity</span>
        </h1>

        <span className="cb-loader" aria-hidden="true" />

        <ol className="cb-steps" aria-label="Sign-in progress">
          {STEPS.map((label, i) => (
            <li
              key={label}
              className={`cb-step${i < stage ? ' cb-step--done' : ''}${
                i === stage ? ' cb-step--active' : ''
              }`}
              aria-current={i === stage ? 'step' : undefined}
            >
              <span className="cb-step-dot" />
              <span className="cb-step-label">{label}</span>
            </li>
          ))}
        </ol>

        <p className="cb-footnote">Signing in with Microsoft · secured by OAuth 2.0</p>
      </div>
    </div>
  );
}

export default CallbackPage;
